import React, { useState, useEffect, useMemo } from 'react';
import { usePerformanceMonitor } from '../../../hooks/weather/usePerformanceMonitor';
import styles from './ModernDateTime.module.scss';

interface ModernDateTimeProps {
  timezone?: string;
  weatherCondition?: 'clear-day' | 'clear-night' | 'cloudy' | 'partly-cloudy' | 'rain' | 'snow' | 'fog' | 'default';
}

export const ModernDateTime = React.memo(({ 
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  weatherCondition = 'default'
}: ModernDateTimeProps) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  // Performance monitoring
  usePerformanceMonitor(
    { componentName: 'ModernDateTime', trackProps: true },
    [timezone, weatherCondition]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Memoize date formatting options to prevent recreation on every render
  const dateFormatOptions = useMemo((): Intl.DateTimeFormatOptions => ({
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: timezone
  }), [timezone]);

  const timeFormatOptions = useMemo((): Intl.DateTimeFormatOptions => ({
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: timezone,
    hour12: false
  }), [timezone]);

  const formattedDate = useMemo(() => {
    return currentTime.toLocaleDateString('es-ES', dateFormatOptions);
  }, [currentTime, dateFormatOptions]);

  const timeComponents = useMemo(() => {
    const timeString = currentTime.toLocaleTimeString('es-ES', timeFormatOptions);
    const [hours, minutes, seconds] = timeString.split(':');
    return { hours, minutes, seconds };
  }, [currentTime, timeFormatOptions]);

  return (
    <div className={`${styles.modernDateTime} ${styles[`modernDateTime--${weatherCondition}`]}`}>
      <div className={styles.modernDateTime__date}>
        {formattedDate}
      </div>
      <div className={styles.modernDateTime__time}>
        <span className={styles.modernDateTime__timeSegment}>{timeComponents.hours}</span>
        <span className={styles.modernDateTime__separator}>:</span>
        <span className={styles.modernDateTime__timeSegment}>{timeComponents.minutes}</span>
        <span className={styles.modernDateTime__separator}>:</span>
        <span className={styles.modernDateTime__timeSegment}>{timeComponents.seconds}</span>
      </div>
    </div>
  );
});